<template>
  <div :class="darkMode ? 'dark' : ''" class="min-h-screen font-sans">
    <div class="flex flex-col h-screen bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-100">

      <header
        class="flex-shrink-0 flex items-center justify-between px-4 sm:px-6 h-16 bg-white/80 dark:bg-slate-800/50 backdrop-blur-sm border-b border-slate-200 dark:border-slate-700 shadow-sm sticky top-0 z-40">
        <div class="flex items-center">
          <button @click="toggleMenu" class="md:hidden btn-icon mr-2" aria-label="Alternar menu">
            <span class="material-symbols-outlined text-xl leading-none">menu</span>
          </button>
          <router-link to="/user/dashboard" class="flex items-center text-purple-600 dark:text-purple-400">
            <span class="material-symbols-outlined mr-2 text-3xl">show_chart</span>
            <span class="text-xl font-bold">ControlEase</span>
          </router-link>
        </div>

        <nav class="flex space-x-2 items-center">
          <button @click="toggleDarkMode" aria-label="Alternar modo escuro" class="btn-icon">
            <span class="material-symbols-outlined text-xl leading-none">{{ darkMode ? 'light_mode' : 'dark_mode'
              }}</span>
          </button>

          <div class="relative" ref="profileMenuContainer">
            <button @click="isProfileMenuOpen = !isProfileMenuOpen" aria-label="Meu Perfil" class="btn-icon">
              <span class="material-symbols-outlined text-xl leading-none">person</span>
            </button>
            <transition name="modal-fade">
              <div v-if="isProfileMenuOpen"
                class="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-lg shadow-xl border dark:border-slate-700 py-1 z-50">
                <a @click="logout"
                  class="context-menu-item text-red-600 dark:text-red-400 hover:!bg-red-50 dark:hover:!bg-red-500/10">
                  <span class="material-symbols-outlined mr-3 text-lg">logout</span>
                  <span>Sair</span>
                </a>
              </div>
            </transition>
          </div>
        </nav>
      </header>

      <div class="flex flex-1 overflow-y-hidden">
        <aside @click.stop :class="[
          'flex-shrink-0 w-64',
          'fixed inset-y-0 left-0 top-16 z-30 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 py-4 flex flex-col transition-transform duration-300 ease-in-out',
          'md:relative md:top-0 md:shadow-none md:translate-x-0',
          menuAberto ? 'translate-x-0' : '-translate-x-full'
        ]">
          <div class="flex-1 px-4 space-y-4 overflow-y-auto">
            <div v-for="group in menuItems" :key="group.title">
              <h3 class="px-3 text-xs font-semibold uppercase text-slate-400 dark:text-slate-500 tracking-wider">{{
                group.title }}</h3>
              <nav class="mt-2 space-y-1">
                <router-link v-for="item in group.items" :key="item.path" :to="item.path" class="menu-item group"
                  active-class="menu-item-active" @click="fecharMenuAoClicarLink">
                  <span class="material-symbols-outlined mr-3 text-xl transition-colors">{{ item.icon }}</span>
                  <span>{{ item.label }}</span>
                </router-link>
              </nav>
            </div>
          </div>
          <div class="px-4 pt-4 border-t border-slate-200 dark:border-slate-700">
            <a @click="logout"
              class="menu-item group text-red-600 dark:text-red-400 hover:!bg-red-50 dark:hover:!bg-red-500/10">
              <span class="material-symbols-outlined mr-3 text-xl">logout</span>
              <span>Sair</span>
            </a>
          </div>
        </aside>

        <main class="flex flex-col flex-grow p-4 sm:p-6 overflow-y-auto" @click="fecharMenuSeAbertoFora">
          <div class="flex-grow">
            <router-view />
          </div>
          <footer
            class="flex-shrink-0 pt-8 mt-8 border-t border-slate-200 dark:border-slate-700 text-center text-xs sm:text-sm text-slate-500 dark:text-slate-400">
            © {{ new Date().getFullYear() }} ControlEase. Todos os direitos reservados.
          </footer>
        </main>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const menuAberto = ref(false)
const isProfileMenuOpen = ref(false)
const profileMenuContainer = ref(null)
const darkMode = ref(localStorage.getItem('darkMode') === 'true')


const menuItems = ref([
  {
    title: 'Principal',
    items: [
      { path: '/admin/dashboard', label: 'Painel Principal', icon: 'dashboard' },
      { path: '/admin/usuarios', label: 'Gerenciar Usuários', icon: 'group' },
    ]
  },
]);

const toggleMenu = () => menuAberto.value = !menuAberto.value;
const fecharMenuAoClicarLink = () => { if (window.innerWidth < 768) menuAberto.value = false; };
const fecharMenuSeAbertoFora = () => { if (menuAberto.value) menuAberto.value = false; };


const logout = () => {
  localStorage.clear();
  router.push('/');
  isProfileMenuOpen.value = false;
};

const toggleDarkMode = () => {
  darkMode.value = !darkMode.value
  localStorage.setItem('darkMode', darkMode.value.toString());
  document.documentElement.classList.toggle('dark', darkMode.value);
};

watch(() => router.currentRoute.value.path, () => {
  fecharMenuAoClicarLink();
}, { immediate: true });

const handleClickOutsideProfileMenu = (event) => {
  if (isProfileMenuOpen.value && profileMenuContainer.value && !profileMenuContainer.value.contains(event.target)) {
    isProfileMenuOpen.value = false;
  }
};

watch(isProfileMenuOpen, (isOpen) => {
  if (isOpen) {
    document.addEventListener('click', handleClickOutsideProfileMenu, true);
  } else {
    document.removeEventListener('click', handleClickOutsideProfileMenu, true);
  }
});
</script>

<style>
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.dark ::-webkit-scrollbar-thumb {
  background: #475569;
}

.dark ::-webkit-scrollbar-thumb:hover {
  background: #334155;
}

* {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.dark * {
  scrollbar-color: #475569 transparent;
}

.material-symbols-outlined {
  vertical-align: middle;
}

.stat-card {
  @apply flex items-center gap-x-3 bg-slate-100 dark:bg-slate-700/50 p-2 rounded-lg;
}

.stat-label {
  @apply text-xs text-slate-500 dark:text-slate-400;
}

.stat-value {
  @apply text-sm font-semibold;
}

.btn-icon {
  @apply text-slate-600 dark:text-slate-400 hover:text-purple-600 dark:hover:text-purple-300 p-2 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors;
}

.menu-item {
  @apply flex items-center px-3 py-2.5 text-sm font-medium rounded-lg text-slate-700 dark:text-slate-300 hover:bg-purple-50 dark:hover:bg-slate-700 hover:text-purple-600 dark:hover:text-purple-300 transition-colors cursor-pointer;
}

.menu-item-active {
  @apply bg-purple-100 dark:bg-purple-700/30 text-purple-700 dark:text-purple-300 font-semibold;
}

.context-menu-item {
  @apply flex items-center w-full px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-10px);
}
</style>